<template>
  <div class="resources-page">
    <AppHeader />

    <ResourcesBanner />

    <div class="resources-page__body">
      <div class="resources-page__inner">
        <ResourcesSidebar
          :resource-types="resourceTypes"
          :authors="authorFilters"
          :languages="languageFilters"
          :active-resource-type.sync="filters.resourceType"
          :active-author.sync="filters.author"
          :active-language.sync="filters.language"
        />

        <div class="resources-page__content">
          <SearchPanel
            ref="searchPanelRef"
            :view-mode.sync="viewMode"
            :total="total"
            :active-tags="activeTags"
            :filter-tags="filterTags"
            @search="handleSearch"
            @secondary-search="handleSecondarySearch"
            @remove-tag="removeTag"
            @remove-filter-tag="removeFilterTag"
            @clear-tags="clearTags"
          />

          <ResultList
            v-loading="loading"
            :list="list"
            :view-mode="viewMode"
            @need-login="loginVisible = true"
          />

          <div v-if="total" class="resources-page__pagination">
            <el-pagination
              :current-page.sync="currentPage"
              :page-size="pageSize"
              :total="total"
              layout="prev, pager, next"
              background
              @current-change="fetchList"
            />
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
    <LoginDialog v-model="loginVisible" />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import ResourcesBanner from './components/ResourcesBanner.vue'
import ResourcesSidebar from './components/ResourcesSidebar.vue'
import SearchPanel from './components/SearchPanel.vue'
import ResultList from './components/ResultList.vue'
import LoginDialog from '@/components/common/LoginDialog.vue'
import {
  searchResources,
  fetchResourceFacets,
  mapResourceItem,
  mapResourceTypeFacets,
  mapFacetList
} from '@/api/resources.js'
import {
  authorFilters as defaultAuthorFilters,
  languageFilters as defaultLanguageFilters
} from './data.js'

export default {
  name: 'ResourcesPage',
  components: {
    AppHeader,
    AppFooter,
    ResourcesBanner,
    ResourcesSidebar,
    SearchPanel,
    ResultList,
    LoginDialog
  },
  data() {
    return {
      resourceTypes: [],
      authorFilters: defaultAuthorFilters,
      languageFilters: defaultLanguageFilters,
      activeTags: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      list: [],
      loading: false,
      loginVisible: false,
      viewMode: 'list',
      filters: {
        resourceType: '',
        author: '',
        language: ''
      },
      searchParams: {
        keyword: '',
        matchMode: 'fuzzy',
        secondaryKeyword: '',
        searchMode: 'fuzzy',
        searchConditions: []
      }
    }
  },
  computed: {
    filterTags() {
      const tags = []
      const typeItem = this.resourceTypes.find((item) => item.key === this.filters.resourceType)
      if (typeItem) {
        tags.push({ type: 'resourceType', key: typeItem.key, label: `资源类别：${typeItem.label}` })
      }
      const authorItem = this.authorFilters.find((item) => item.key === this.filters.author)
      if (authorItem) {
        tags.push({ type: 'author', key: authorItem.key, label: `作者：${authorItem.label}` })
      }
      const languageItem = this.languageFilters.find((item) => item.key === this.filters.language)
      if (languageItem) {
        tags.push({ type: 'language', key: languageItem.key, label: `语种：${languageItem.label}` })
      }
      return tags
    }
  },
  watch: {
    filters: {
      handler() {
        this.currentPage = 1
        this.fetchData()
      },
      deep: true
    },
    '$route.query.keyword'(value) {
      this.applyKeywordFromRoute(value)
    }
  },
  mounted() {
    document.documentElement.classList.add('hide-scrollbar')
    this.applyKeywordFromRoute(this.$route.query.keyword, true)
  },
  activated() {
    document.documentElement.classList.add('hide-scrollbar')
  },
  deactivated() {
    document.documentElement.classList.remove('hide-scrollbar')
  },
  beforeDestroy() {
    document.documentElement.classList.remove('hide-scrollbar')
  },
  methods: {
    applyKeywordFromRoute(keyword, fetchOnEmpty = false) {
      const normalized = (keyword || '').trim()
      if (normalized) {
        this.$nextTick(() => {
          if (this.$refs.searchPanelRef) {
            this.$refs.searchPanelRef.setKeyword(normalized)
          }
        })
        this.handleSearch({ keyword: normalized, matchMode: this.searchParams.matchMode || 'fuzzy' })
        return
      }
      if (fetchOnEmpty) {
        this.fetchData()
      }
    },
    buildQueryParams() {
      return {
        page: this.currentPage,
        limit: this.pageSize,
        resourceType: this.filters.resourceType || undefined,
        author: this.filters.author || undefined,
        language: this.filters.language || undefined,
        keyword: this.searchParams.keyword || undefined,
        secondaryKeyword: this.searchParams.secondaryKeyword || undefined,
        matchMode: this.searchParams.matchMode || 'fuzzy',
        searchConditions: Array.isArray(this.searchParams.searchConditions) && this.searchParams.searchConditions.length
          ? this.searchParams.searchConditions
          : undefined
      }
    },
    async fetchData() {
      this.loading = true
      try {
        const params = this.buildQueryParams()
        const [pageRes, facetsRes] = await Promise.all([
          searchResources(params),
          fetchResourceFacets(params)
        ])
        this.list = (pageRes.records || []).map(mapResourceItem)
        this.total = pageRes.total || 0
        this.resourceTypes = mapResourceTypeFacets(facetsRes.resourceTypes)
        this.authorFilters = mapFacetList(facetsRes.authors, defaultAuthorFilters)
        this.languageFilters = mapFacetList(facetsRes.languages, defaultLanguageFilters)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    fetchList() {
      this.fetchData()
    },
    handleSearch(params) {
      this.searchParams.matchMode = params.matchMode || 'fuzzy'
      this.searchParams.secondaryKeyword = ''
      this.currentPage = 1
      if (params.searchMode === 'advanced') {
        const conditions = Array.isArray(params.conditions) ? params.conditions : []
        this.searchParams.searchMode = 'advanced'
        this.searchParams.keyword = ''
        this.searchParams.searchConditions = conditions
        this.activeTags = conditions.map((item) => this.formatConditionTag(item))
      } else {
        this.searchParams.searchMode = 'fuzzy'
        this.searchParams.keyword = params.keyword || ''
        this.searchParams.searchConditions = []
        this.activeTags = params.keyword ? [params.keyword] : []
      }
      this.fetchData()
    },
    formatConditionTag(item) {
      const fieldLabel = item.field === 'author' ? '作者' : '资源名称'
      return `${fieldLabel}：${item.keyword}`
    },
    parseConditionTag(tag) {
      const matched = String(tag || '').match(/^(资源名称|作者)：(.+)$/)
      if (!matched) {
        return null
      }
      return {
        field: matched[1] === '作者' ? 'author' : 'title',
        keyword: matched[2]
      }
    },
    handleSecondarySearch(params) {
      this.searchParams.secondaryKeyword = params.keyword
      this.currentPage = 1
      if (params.keyword && !this.activeTags.includes(params.keyword)) {
        this.activeTags = [...this.activeTags, params.keyword]
      }
      this.fetchData()
    },
    removeTag(tag) {
      this.activeTags = this.activeTags.filter((item) => item !== tag)
      if (this.searchParams.searchMode === 'advanced') {
        const parsed = this.parseConditionTag(tag)
        const conditions = (this.searchParams.searchConditions || []).filter((item) => {
          if (!parsed) {
            return this.formatConditionTag(item) !== tag
          }
          return !(item.field === parsed.field && item.keyword === parsed.keyword)
        })
        this.searchParams.searchConditions = conditions
        if (this.$refs.searchPanelRef) {
          this.$refs.searchPanelRef.setConditions(conditions)
        }
      } else {
        if (this.searchParams.keyword === tag) {
          this.searchParams.keyword = ''
        }
        if (this.searchParams.secondaryKeyword === tag) {
          this.searchParams.secondaryKeyword = ''
        }
      }
      this.currentPage = 1
      this.fetchData()
    },
    removeFilterTag(tag) {
      if (!tag || !tag.type) {
        return
      }
      if (tag.type === 'resourceType') {
        this.filters.resourceType = ''
      } else if (tag.type === 'author') {
        this.filters.author = ''
      } else if (tag.type === 'language') {
        this.filters.language = ''
      }
    },
    clearTags() {
      this.activeTags = []
      this.searchParams.keyword = ''
      this.searchParams.secondaryKeyword = ''
      this.searchParams.searchMode = 'fuzzy'
      this.searchParams.searchConditions = []
      this.filters.resourceType = ''
      this.filters.author = ''
      this.filters.language = ''
      this.currentPage = 1
      if (this.$refs.searchPanelRef) {
        this.$refs.searchPanelRef.reset()
      }
      this.fetchData()
    }
  }
}
</script>

<style lang="scss" scoped>
.resources-page {
  min-height: 100vh;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper;

  &__body {
    padding: $spacing-xl $content-padding $spacing-4xl;
  }

  &__inner {
    display: flex;
    gap: $spacing-xl;
    max-width: $container-max-width;
    margin: 0 auto;
    align-items: flex-start;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: $spacing-xl;
    padding-top: $spacing-base;
  }
}

@media (max-width: 960px) {
  .resources-page__inner {
    flex-direction: column;
  }
}
</style>
